import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Loader2, Sparkles, AlertCircle, CheckCircle2, Pause, Play } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBlogAISettingsPage() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/api/admin/blog/ai-settings`),
      axios.get(`${API_URL}/api/admin/blog/generation-log?limit=20`),
    ])
      .then(([s, l]) => { setSettings(s.data); setLog(l.data); })
      .catch(() => toast.error('Failed to load AI settings'))
      .finally(() => setLoading(false));
  }, []);

  const update = (field, value) => setSettings((p) => ({ ...p, [field]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        text_provider: settings.text_provider,
        image_provider: settings.image_provider,
        fallback_image_provider: settings.fallback_image_provider,
        paused: !!settings.paused,
        monthly_cap: Number(settings.monthly_cap) || 20,
        scheduler_enabled: !!settings.scheduler_enabled,
        scheduler_days: settings.scheduler_days,
        scheduler_hour_uk: Number(settings.scheduler_hour_uk) || 7,
      };
      await axios.put(`${API_URL}/api/admin/blog/ai-settings`, payload);
      toast.success('Settings saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePause = async () => {
    const newPaused = !settings.paused;
    setSettings((p) => ({ ...p, paused: newPaused }));
    try {
      await axios.put(`${API_URL}/api/admin/blog/ai-settings`, { paused: newPaused });
      toast.success(newPaused ? 'AI generation paused' : 'AI generation resumed');
    } catch {
      toast.error('Failed');
    }
  };

  if (loading) {
    return (
      <PageLayout showNav={false}>
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      </PageLayout>
    );
  }

  const capUsedPct = Math.min(100, Math.round((settings.used_this_month / Math.max(1, settings.monthly_cap)) * 100));

  return (
    <PageLayout showNav={false}>
      <div className="px-4 py-6 max-w-2xl mx-auto" data-testid="ai-settings-page">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin/blog" className="text-text-secondary hover:text-primary"><ArrowLeft className="w-5 h-5" /></Link>
          <Sparkles className="w-6 h-6 text-primary" />
          <h1 className="font-heading font-bold text-dark text-2xl">AI Blog Settings</h1>
        </div>

        {/* Pause toggle + monthly cap status */}
        <div className={`card-default p-5 mb-4 border-l-4 ${settings.paused ? 'border-alert' : 'border-primary'}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <p className="font-semibold text-dark">
                {settings.paused ? 'AI generation is PAUSED' : 'AI generation is ACTIVE'}
              </p>
              <p className="text-xs text-text-light mt-1">
                {settings.paused
                  ? 'No AI drafts will be generated, manually or by scheduler.'
                  : 'You and the scheduler can generate drafts (within monthly cap).'}
              </p>
            </div>
            <Button onClick={togglePause} className={settings.paused ? 'btn-primary' : 'btn-secondary'} data-testid="ai-pause-toggle">
              {settings.paused ? <><Play className="w-4 h-4 mr-1" /> Resume</> : <><Pause className="w-4 h-4 mr-1" /> Pause</>}
            </Button>
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-xs mb-1">
              <span className="text-text-secondary">This month</span>
              <span className="font-semibold">{settings.used_this_month} / {settings.monthly_cap}</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full ${capUsedPct >= 90 ? 'bg-alert' : 'bg-primary'}`} style={{ width: `${capUsedPct}%` }} />
            </div>
          </div>
        </div>

        {/* Provider settings */}
        <div className="card-default p-5 mb-4 space-y-4">
          <h2 className="font-heading font-semibold text-dark">Providers</h2>

          <div>
            <label className="text-xs text-text-light font-medium">Text generation provider</label>
            <select value={settings.text_provider} onChange={(e) => update('text_provider', e.target.value)} className="input-field w-full" data-testid="text-provider-select">
              <option value="openai">OpenAI (GPT)</option>
              <option value="anthropic">Claude (Sonnet 4.5)</option>
              <option value="gemini">Gemini</option>
            </select>
            {settings.openai_user_key_set && settings.text_provider === 'openai' && (
              <p className="text-xs text-primary mt-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Using your own OpenAI API key
              </p>
            )}
          </div>

          <div>
            <label className="text-xs text-text-light font-medium">Image generation provider</label>
            <select value={settings.image_provider} onChange={(e) => update('image_provider', e.target.value)} className="input-field w-full" data-testid="image-provider-select">
              <option value="openai">OpenAI (GPT Image 1)</option>
              <option value="gemini">Gemini (Nano Banana)</option>
            </select>
          </div>

          <div>
            <label className="text-xs text-text-light font-medium">Image fallback (if primary fails)</label>
            <select value={settings.fallback_image_provider} onChange={(e) => update('fallback_image_provider', e.target.value)} className="input-field w-full" data-testid="image-fallback-select">
              <option value="gemini">Gemini (Nano Banana)</option>
              <option value="openai">OpenAI (GPT Image 1)</option>
            </select>
          </div>
        </div>

        {/* Safety net */}
        <div className="card-default p-5 mb-4 space-y-4">
          <h2 className="font-heading font-semibold text-dark">Safety Net</h2>
          <div>
            <label className="text-xs text-text-light font-medium">Monthly generation cap</label>
            <Input type="number" min="0" max="500" value={settings.monthly_cap} onChange={(e) => update('monthly_cap', e.target.value)} className="input-field" data-testid="monthly-cap-input" />
            <p className="text-xs text-text-light mt-1">Hard limit. When hit, no new drafts are generated until next month or you raise this.</p>
          </div>
        </div>

        {/* Scheduler */}
        <div className="card-default p-5 mb-4 space-y-4">
          <h2 className="font-heading font-semibold text-dark">Auto-scheduler</h2>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.scheduler_enabled}
              onChange={(e) => update('scheduler_enabled', e.target.checked)}
              className="w-4 h-4"
              data-testid="scheduler-enabled-toggle"
            />
            <span className="text-sm text-dark">Enable auto-scheduler (Mon/Wed/Fri 7am UK)</span>
          </label>
          <p className="text-xs text-text-light">
            Always saves as Draft. You review and publish manually. Disable any time.
          </p>
          <div className="text-xs text-text-light bg-gold-light/50 rounded-lg p-3 flex gap-2">
            <AlertCircle className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" />
            <span>This scheduler also requires <code className="bg-white px-1 rounded">ENABLE_REENGAGEMENT_SCHEDULER=true</code> in production env vars (already on in preview).</span>
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="btn-primary w-full py-3" data-testid="save-ai-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Save Settings'}
        </Button>

        {/* Generation log */}
        <div className="mt-8">
          <h2 className="font-heading font-semibold text-dark mb-3">Recent Generations</h2>
          {log.length === 0 ? (
            <p className="text-sm text-text-light">No generations yet.</p>
          ) : (
            <div className="space-y-2">
              {log.map((entry) => (
                <div key={entry.id} className="card-default p-3 text-sm" data-testid={`log-entry-${entry.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-dark truncate">{entry.title || entry.topic_or_keyword}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${entry.success ? 'bg-primary-light text-primary' : 'bg-alert/10 text-alert'}`}>
                      {entry.success ? 'OK' : 'Failed'}
                    </span>
                  </div>
                  <p className="text-xs text-text-light mt-1">
                    {entry.source} · {entry.provider || '—'} · {entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                  </p>
                  {entry.error && <p className="text-xs text-alert mt-1">{entry.error}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
